import { useEffect, useState, type FormEvent, type ReactElement } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import { ResendVerification } from "../components/ResendVerification";

// Short codes the SSO callback may land here with (details stay server-side).
const SSO_ERRORS: Record<string, string> = {
  denied: "SSO sign-in was cancelled.",
  forbidden: "Your MGB account has no FaceAge role assigned. Contact the study team.",
  failed: "SSO sign-in failed. Please try again.",
};

export function Login(): ReactElement {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const ssoError = SSO_ERRORS[searchParams.get("sso_error") ?? ""] ?? null;

  useEffect(() => {
    api<{ enabled: boolean }>("GET", "/auth/sso/enabled", { auth: false })
      .then((r) => setSsoEnabled(r.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setNeedsVerify(false);
    setBusy(true);
    try {
      const user = await login(email, password);
      const home =
        user.role === "admin" ? "/admin" : user.role === "collaborator" ? "/collab" : "/app";
      navigate(home, { replace: true });
    } catch (err) {
      // 403 = correct credentials but the email isn't verified yet — offer a resend.
      if (err instanceof ApiError && err.status === 403) setNeedsVerify(true);
      setError(err instanceof ApiError ? err.message : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your FaceAge account.">
      <form onSubmit={onSubmit} className="auth-form">
        <label className="field">
          <span>Email or study ID</span>
          <input
            type="text"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com or your study ID"
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        {ssoError ? <p className="form-error">{ssoError}</p> : null}
        {needsVerify ? <ResendVerification email={email} /> : null}
        <button type="submit" className="btn-primary-glow btn-block" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      {ssoEnabled ? (
        <p className="auth-alt">
          {/* Full-page navigation on purpose — the OIDC redirect can't be a fetch. */}
          <a className="btn-ghost btn-block" href="/auth/sso/login">
            Sign in with MGB SSO
          </a>
        </p>
      ) : null}
      <p className="auth-alt">
        <Link to="/forgot-password">Forgot password?</Link>
      </p>
      <p className="auth-alt muted">
        Returning from the previous FaceAge website? Use &ldquo;Forgot
        password?&rdquo; with your old email to set up your access &mdash; your
        study data will be waiting.
      </p>
      <p className="auth-alt">
        New here? <Link to="/register">Join the study</Link>
      </p>
    </AuthShell>
  );
}
